import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams, Link, useLocation, Navigate } from 'react-router-dom';
import { filter } from 'lodash';
import { sentenceCase } from 'change-case';
import { useEffect, useState } from 'react';
import { MdOutlineEdit } from 'react-icons/md';
import { IoEyeOutline } from 'react-icons/io5';
import { CiTrash } from 'react-icons/ci';
import { FaRegCopy } from 'react-icons/fa';
import InfoTooltip from '../components/InfoTooltip';
// @mui
import {
  Card,
  Table,
  Stack,
  Paper,
  Avatar,
  Button,
  Popover,
  Checkbox,
  TableRow,
  MenuItem,
  TableBody,
  TableCell,
  Container,
  Typography,
  IconButton,
  TableContainer,
  TablePagination,
  CircularProgress,
} from '@mui/material';

import Swal from 'sweetalert2';
import Label from '../components/label';
import Iconify from '../components/iconify';
import Scrollbar from '../components/scrollbar';
// sections
import { UserListHead } from '../sections/@dashboard/user';
// mock
import appConfig from '../config';
import { deleteContractor, getAllContractor } from '../helper/backend_helper';

const TABLE_HEAD = [
  { id: 'name', label: 'Name', alignRight: true },
  { id: 'completedPercentage', label: 'Completed Percentage', alignRight: false },
  { id: 'lastOpenedAt', label: 'Last Opened', alignRight: false },
  { id: 'dealInclusiveEnabled', label: 'Deal Inclusive', alignRight: false },
  { id: 'percentage', label: 'Percentage', alignRight: false },
  { id: 'contractDuration', label: 'Contract Duration', alignRight: false },
  { id: 'contractTemplateName', label: 'Template Name', alignRight: false },
  { id: 'propertiesCount', label: 'Properties Count', alignRight: false },
  { id: 'copyLink', label: 'Copy Link', alignRight: false },
  { id: 'actions', label: 'Actions', alignRight: false },
];

// ----------------------------------------------------------------------

function getContractSortValue(row, orderBy) {
  // Some contractor columns are derived in the UI, so sorting must read the same displayed values.
  switch (orderBy) {
    case 'completedPercentage':
      return calculateFilledFormPercentNumber(row);
    case 'lastOpenedAt':
      return row.lastOpenedAt ? new Date(row.lastOpenedAt).getTime() : 0;
    case 'dealInclusiveEnabled':
      return row.allInclusiveContractTemplate ? 1 : 0;
    case 'contractTemplateName':
      return row.contractTemplate?.name || '';
    case 'propertiesCount':
      return row.properties?.length || 0;
    case 'copyLink':
    case 'actions':
      return '';
    default:
      return row[orderBy] ?? '';
  }
}

function descendingComparator(a, b, orderBy) {
  const valueA = getContractSortValue(a, orderBy);
  const valueB = getContractSortValue(b, orderBy);

  if (valueB < valueA) {
    return -1;
  }
  if (valueB > valueA) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  if (query) {
    return filter(stabilizedThis.map((el) => el[0]), (_user) => {
      const searchableName = [_user.name, _user.firstName, _user.lastName]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return searchableName.indexOf(query.toLowerCase()) !== -1;
    });
  }
  return stabilizedThis.map((el) => el[0]);
}

export default function Contractor() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(null);

  const [page, setPage] = useState(0);

  const [order, setOrder] = useState('desc');

  const [selected, setSelected] = useState([]);

  const [orderBy, setOrderBy] = useState('completedPercentage');

  const [filterName, setFilterName] = useState('');

  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [clickedRowData, setClickedRowData] = useState(null);
  const [users, setUsers] = useState([]);

  async function fetchData() {
    try {
      setLoading(true);

      const headers = {
        headers: {
          'Content-Type': 'application/json',
        },
      };
      const data = {
        isSubmitted: false,
      };

      const usersResponse = await getAllContractor(data, headers);
      const contracts = usersResponse?.data?.data || [];
      // Put the contracts closest to completion first so follow-up work is easier to prioritize.
      setUsers([...contracts].sort((a, b) => calculateFilledFormPercentNumber(b) - calculateFilledFormPercentNumber(a)));
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchData();
  }, []);

  const [loading, setLoading] = useState(false);

  const handleDelete = async (_id) => {
    try {
      const confirmed = await Swal.fire({
        icon: 'warning',
        title: 'Are you sure you want to delete this Contractor?',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, delete it!',
        cancelButtonText: 'No, cancel!',
      });

      if (confirmed.isConfirmed) {
        const headers = {
          headers: {
            'Content-Type': 'application/json',
          },
        };

        const response = await deleteContractor(_id, headers);
        Swal.fire({
          icon: 'success',
          title: 'Contractor deleted successfully',
        });
        fetchData();
      }
    } catch (error) {
      console.log(error);
      Swal.fire({
        icon: 'error',
        title: 'An error occurred while deleting',
      });
    }
  };

  const handleCloseMenu = () => {
    setOpen(null);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setPage(0);
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  const handleFilterByName = (event) => {
    setPage(0);
    setFilterName(event.target.value);
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - users.length) : 0;

  const filteredUsers = applySortFilter(users, getComparator(order, orderBy), filterName);

  const isNotFound = !filteredUsers.length && !!filterName;
  // const encodedData = encodeURIComponent(JSON.stringify(clickedRow));
  return (
    <>
      <Helmet>
        <title> Contractor </title>
      </Helmet>

      <Card sx={{ p: 3 }}>
        <Stack mb={3} direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h4">Contracts{' '}<InfoTooltip width={20} title="The working list of contractor onboarding forms, sorted by how close each is to completion. Each row shows the contractor's name, completion percentage, when they last opened their form, their commission percentage, contract duration, template and number of properties. Use this page to add a contractor, edit or continue their contract, copy their personal contract link to share, or delete an entry." /></Typography>

          <Button
            sx={{
              boxShadow: 'none',
              backgroundColor: '#1E3A5F',
              '&:hover': {
                backgroundColor: '#1E3A5F',
                boxShadow: 'none',
              },
            }}
            variant="contained"
            endIcon={<Iconify icon="eva:plus-fill" />}
            onClick={() => navigate('/contractorList/add_contractor')}
          >
            Add Contractor
          </Button>
        </Stack>

        <Scrollbar>
          <TableContainer sx={{ minWidth: 800 }}>
            <Table>
              <UserListHead
                order={order}
                orderBy={orderBy}
                headLabel={TABLE_HEAD}
                rowCount={users.length}
                numSelected={selected.length}
                onRequestSort={handleRequestSort}
              />
              <TableBody>
                {filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => {
                  console.log(row);
                  const { _id, name, percentage, contractDuration, contractTemplate } = row;

                  return (
                    <TableRow hover key={_id}>
                      <TableCell component="th" scope="row">
                        {/* <Avatar alt={name} src={`${appConfig.FILe_URL}${directory}/${image}`} /> */}
                        {/* <Avatar alt={name} src={image} /> */}
                        <Typography align="left" variant="subtitle2" noWrap>
                          {name}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">{calculateFilledFormPercent(row)}</TableCell>
                      <TableCell align="center">{formatLastOpened(row.lastOpenedAt)}</TableCell>
                      <TableCell align="center">{row.allInclusiveContractTemplate ? 'Yes' : 'No'}</TableCell>
                      <TableCell align="center">{percentage} %</TableCell>
                      <TableCell align="center">{contractDuration}</TableCell>
                      <TableCell align="center">{contractTemplate?.name}</TableCell>
                      <TableCell align="center">{row?.properties?.length}</TableCell>

                      <TableCell align="center">
                        <Stack sx={{ cursor: 'pointer' }}>
                          <IconButton
                            onClick={() => navigator.clipboard.writeText(`${appConfig.CONTACT_URL}/contract/${_id}`)}
                            size="large"
                            title="Copy Link"
                          >
                            <FaRegCopy size={20} color="#009245" sx={{ cursor: 'pointer' }} />
                          </IconButton>
                        </Stack>
                      </TableCell>

                      <TableCell align="center">
                        <Stack
                          sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          {/* <Button
                               onClick={() => navigate('/contractorList/contract-detail', { state: { userData: row } })}
                              sx={{
                                boxShadow: 'none',
                                backgroundColor: 'secondary.main',
                                color: '#fff',
                                '&:hover': {
                                  backgroundColor: 'secondary.main',
                                  boxShadow: 'none',
                                },
                              }}
                              size="small"
                              variant="contained"
                              startIcon={<IoEyeOutline  />}
                            >
                              Detail 
                            </Button> */}
                          <Button
                            onClick={() => navigate('/contractorList/update_contractor', { state: { userData: row } })}
                            sx={{
                              boxShadow: 'none',
                              backgroundColor: '#B5FFD8',
                              color: '#009245',
                              '&:hover': {
                                backgroundColor: '#B5FFD8',
                                boxShadow: 'none',
                              },
                            }}
                            size="small"
                            variant="contained"
                            startIcon={<MdOutlineEdit />}
                          >
                            Edit
                          </Button>

                          <Button
                            onClick={(event) => handleDelete(_id)}
                            sx={{
                              boxShadow: 'none',
                              backgroundColor: '#FFC6C6',
                              color: '#DC2626',
                              '&:hover': {
                                backgroundColor: '#FFC6C6',
                                boxShadow: 'none',
                              },
                            }}
                            size="small"
                            variant="contained"
                            startIcon={<CiTrash />}
                          >
                            Delete
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {emptyRows > 0 && (
                  <TableRow style={{ height: 53 * emptyRows }}>
                    <TableCell colSpan={7} />
                  </TableRow>
                )}
                {loading && (
                  <TableRow style={{ height: 53 * emptyRows }}>
                    <TableCell colSpan={7}>
                      <CircularProgress size="20px" />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>

              {isNotFound && (
                <TableBody>
                  <TableRow>
                    <TableCell align="center" colSpan={7} sx={{ py: 3 }}>
                      <Paper
                        sx={{
                          textAlign: 'center',
                        }}
                      >
                        <Typography variant="h6" paragraph>
                          Not found
                        </Typography>

                        <Typography variant="body2">
                          No results found for &nbsp;
                          <strong>&quot;{filterName}&quot;</strong>.
                          <br /> Try checking for typos or using complete words.
                        </Typography>
                      </Paper>
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
            </Table>
          </TableContainer>
        </Scrollbar>

        <TablePagination
          rowsPerPageOptions={[100, 200, 300]}
          component="div"
          count={users.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Card>

      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            p: 1,
            width: 140,
            '& .MuiMenuItem-root': {
              px: 1,
              typography: 'body2',
              borderRadius: 0.75,
            },
          },
        }}
      >
        {/*     
        <Link
          
          style={{ textDecoration: 'none' }}
          // to={`/dashboard/user-detail/${encodeURIComponent(JSON.stringify(clickedRow))}`}
          to={`/dashboard/user-detail?data=${encodedData}`}
        > */}
        <MenuItem
          onClick={() => navigate('/contractor/contractor-detail', { state: { clickedRowData } })}
          sx={{ color: 'success.main' }}
        >
          <Iconify icon={'eva:image-outline'} sx={{ mr: 2 }} />
          Detail
        </MenuItem>
        {/* </Link> */}
        <MenuItem>
          <Iconify icon={'eva:edit-outline'} sx={{ mr: 2 }} />
          Block
        </MenuItem>

        <MenuItem sx={{ color: 'error.main' }}>
          <Iconify icon={'eva:trash-2-outline'} sx={{ mr: 2 }} />
          Delete
        </MenuItem>
      </Popover>
    </>
  );
}

/* eslint-disable no-plusplus */
function formatLastOpened(value) {
  if (!value) return 'Never opened';
  const diffMs = Date.now() - new Date(value).getTime();
  const minutes = Math.max(0, Math.floor(diffMs / 60000));
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours} h ago`;
  return new Date(value).toLocaleDateString('it-IT');
}

function calculateFilledFormPercentNumber(state) {
  let totalFields = 0;
  let filledFields = 0;

  const checkField = (field) => {
    totalFields++;
    if (Array.isArray(field)) {
      if (field.length > 0) filledFields++;
    } else if (typeof field === 'string') {
      if (field.trim() !== '') filledFields++;
    } else if (field) {
      filledFields++;
    }
  };

  // Owner info
  if (state.ownerType === 'private' && state.privateOwner) {
    const privateFields = [
      state.privateOwner.firstName,
      state.privateOwner.lastName,
      state.privateOwner.fiscalCode,
      state.privateOwner.documentCopy,
      state.privateOwner.fiscalCodeCopy,
      state.privateOwner.phoneCode,
      state.privateOwner.phone,
      state.privateOwner.gender,
      state.privateOwner.birthDate,
      state.privateOwner.birthPlace,
      state.privateOwner.address,
      state.privateOwner.streetNumber,
      state.privateOwner.city,
      state.privateOwner.postalCode,
      state.privateOwner.province,
      state.privateOwner.country,
      state.privateOwner.email,
      state.privateOwner.documentType,
      state.privateOwner.documentNumber,
      state.privateOwner.documentDetails,
    ];
    privateFields.forEach(checkField);
    // Only count if fiscalCode is 16 characters
    if (state.privateOwner.fiscalCode?.length === 16) filledFields++;
    totalFields++; // for valid fiscalCode
  }
  if (state.ownerType === 'company' && state.companyInfo) {
    const companyFields = [
      state.companyInfo.companyName,
      state.companyInfo.vatNumber,
      state.companyOwner.firstName,
      state.companyOwner.lastName,
      state.companyOwner.documentCopy,
      state.companyOwner.fiscalCodeCopy,
      state.companyOwner.fiscalCode,
      state.companyInfo.phoneCode,
      state.companyInfo.phone,
      state.companyInfo.reaNumber,
      state.companyInfo.legalAddress,
      state.companyInfo.streetNumber,
      state.companyInfo.city,
      state.companyInfo.postalCode,
      state.companyInfo.province,
      state.companyInfo.email,
    ];
    companyFields.forEach(checkField);
    if (state.companyOwner.fiscalCode?.length === 16) filledFields++;
    totalFields++; // for valid fiscalCode
  }

  // Properties
  (state?.properties || []).forEach((property) => {
    const propertyFields = [
      property.address,
      property.streetNumber,
      property.city,
      property.postalCode,
      property.province,
      property?.visuraCatastale,
      property?.visuraCatastaleInfo?.numeroVisura,
      property.visuraCatastaleInfo?.dataVisura,
      property.cadastralInfo?.communeCode,
      property.cadastralInfo?.sheet,
      property.cadastralInfo?.particle,
      property.cadastralInfo?.subordinate,
      property.cadastralInfo?.category,
      property.cadastralInfo?.cadastralIncome,
    ];

    propertyFields.forEach(checkField);

    if (property.hasShownPortal) {
      checkField(property?.policePortal);
      checkField(property?.tourismTaxLoginEmail);
      checkField(property?.tourismTaxLoginPassword);
    }
  });
  // Bank info
  checkField(state.bankInfo?.iban);

  // Signature and contract acceptance
  checkField(state.signatureFile?.length > 0);
  checkField(state.contractAccepted);

  // Final percentage
  return Math.round((filledFields / totalFields) * 100);
}

function calculateFilledFormPercent(state) {
  return `${calculateFilledFormPercentNumber(state)}%`;
}

